import { ROOT } from '../env'
import { Logger, PlayerSlot } from '../utils'
import { fillAbsClass } from '../styles'
import type { HostPayload, PlayerBridge } from '../types'

export class LiveAudioPlayer implements PlayerBridge {
  private audio: HTMLAudioElement | null = null
  private currentSource = ''
  private retryTimer: ReturnType<typeof setTimeout> | null = null
  private retryAttempt = 0
  private readonly retryDelaysMs = [700, 1200, 1800, 2600, 3600, 5000, 7000, 9000, 12000, 16000, 22000, 30000]
  private slot = new PlayerSlot('live-audio', ROOT)
  private logger = Logger.create('LiveAudioPlayer')
  private readonly postMessage: (msg: string) => void

  constructor(postMessage: (msg: string) => void) {
    this.postMessage = postMessage
  }

  load(data: HostPayload): void {
    const audio = this.ensureAudioElement()
    const sourceChanged = this.currentSource !== data.id

    if (sourceChanged) {
      this.clearRetryTimer()
      this.retryAttempt = 0
    }

    this.currentSource = data.id

    const resumePlayback = (): void => {
      audio.volume = Math.max(0, Math.min(1, data.volume / 100))

      const playPromise = audio.play()
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch((err) => {
          this.logger.warn('live audio playback start blocked', err)
        })
      }

      this.postMessage('playbackReady')
    }

    if (sourceChanged) {
      const onReady = (): void => {
        audio.removeEventListener('canplay', onReady)
        resumePlayback()
      }

      audio.addEventListener('canplay', onReady)
      audio.src = data.id
      audio.load()
      return
    }

    resumePlayback()
  }

  handleCommand(msg: string): boolean {
    const audio = this.audio

    if (msg === 'durationCheck') {
      const duration = audio?.duration ?? 0
      this.postMessage(`durationCheck=${Math.round(Number.isFinite(duration) ? duration : 0)}`)
      return true
    }

    if (msg === 'unMute') {
      if (audio) audio.muted = false
      return true
    }

    if (msg.startsWith('stopVideo')) {
      audio?.pause()
      return true
    }

    if (msg.startsWith('setVolume=')) {
      const volume = parseInt(msg.split('=').pop() || '', 10)
      if (audio && !Number.isNaN(volume)) {
        audio.volume = Math.max(0, Math.min(1, volume / 100))
      }
      return true
    }

    if (msg.startsWith('setPlaybackQuality=')) {
      // Audio streams do not have selectable quality levels.
      return true
    }

    if (msg.startsWith('seekTo=')) {
      // Ignore host seek commands for non-seekable live audio streams.
      return true
    }

    return false
  }

  destroy(): void {
    this.currentSource = ''
    this.clearRetryTimer()
    this.retryAttempt = 0

    if (this.audio) {
      this.audio.pause()
      this.audio.src = ''
      this.audio.removeAttribute('src')
      this.audio.load()
      this.audio = null
    }

    this.slot.destroy()
  }

  private ensureAudioElement(): HTMLAudioElement {
    if (this.audio) return this.audio

    this.slot.show()
    const audio = this.slot.node({
      tag: 'audio',
      key: 'live-audio-element',
      className: fillAbsClass,
      attrs: {
        autoplay: 'autoplay',
        preload: 'auto',
        crossorigin: 'anonymous',
      },
      style: {
        width: '0',
        height: '0',
        opacity: '0',
        pointerEvents: 'none',
      },
    }) as HTMLAudioElement

    audio.controls = false
    audio.muted = false

    audio.addEventListener('timeupdate', () => {
      this.postMessage(`currentTime=${audio.currentTime}`)
    })

    audio.addEventListener('ended', () => {
      this.scheduleRetry('audio-ended')
    })

    audio.addEventListener('error', () => {
      this.scheduleRetry('audio-error')
    })

    audio.addEventListener('loadedmetadata', () => {
      const duration = Number.isFinite(audio.duration) ? Math.round(audio.duration) : 0
      this.postMessage(`durationCheck=${duration}`)
    })

    this.audio = audio
    return audio
  }

  private clearRetryTimer(): void {
    if (this.retryTimer !== null) {
      clearTimeout(this.retryTimer)
      this.retryTimer = null
    }
  }

  private scheduleRetry(reason: string): void {
    if (!this.currentSource) {
      this.postMessage('onPlayerError=2')
      return
    }

    if (this.retryTimer !== null) {
      return
    }

    if (this.retryAttempt >= this.retryDelaysMs.length) {
      this.logger.warn(`Retry limit reached for live audio stream (${reason})`, {
        source: this.currentSource,
        attempts: this.retryAttempt,
      })
      this.postMessage('onPlayerError=2')
      return
    }

    const delay = this.retryDelaysMs[this.retryAttempt] as number
    this.retryAttempt += 1

    this.logger.warn(`Retrying live audio stream in ${delay}ms`, {
      reason,
      attempt: this.retryAttempt,
      source: this.currentSource,
    })

    this.retryTimer = setTimeout(() => {
      this.retryTimer = null
      this.reloadCurrentSource()
    }, delay)
  }

  private reloadCurrentSource(): void {
    const source = this.currentSource
    const audio = this.audio

    if (!source || !audio) {
      this.postMessage('onPlayerError=2')
      return
    }

    audio.src = source
    audio.load()

    const playPromise = audio.play()
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch((err) => {
        this.logger.warn('live audio retry playback blocked', err)
      })
    }
  }
}
